"use client"

import { useState } from "react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Progress } from "@/components/ui/progress"
import {
  Mail,
  MapPin,
  Phone,
  Book,
  GraduationCap,
  Award,
  Trophy,
  Star,
  Calendar,
  Edit,
  Save,
  X,
  BookOpen,
  Clock,
  Target,
  Zap,
  CheckCircle2,
  Medal,
  Flame,
  Brain,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"

export default function StudentProfilePage() {
  const [isEditing, setIsEditing] = useState(false)
  const { getCurrentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: getCurrentUser()!.name,
    email: getCurrentUser()!.email,
    bio: "Computer Science major with a focus on AI and Machine Learning. Passionate about solving real-world problems through technology.",
    major: "Computer Science",
    year: "Junior",
    studentId: "S12345678",
    location: "Education University of Hong Kong",
    phone: "+1 (555) 123-4567",
    avatarUrl: "/placeholder.svg?height=96&width=96",
  })

  // Daily tasks and credits
  const dailyTasks = [
    { id: 1, name: "Attended Algorithms Lecture", date: "Today", credits: 5, completed: true },
    { id: 2, name: "Submitted Math Assignment", date: "Today", credits: 10, completed: true },
    { id: 3, name: "Participated in Group Discussion", date: "Today", credits: 8, completed: true },
    { id: 4, name: "Completed Programming Lab", date: "Yesterday", credits: 15, completed: true },
    { id: 5, name: "Attended Study Group", date: "Yesterday", credits: 7, completed: true },
    { id: 6, name: "Completed Quiz", date: "2 days ago", credits: 12, completed: true },
    { id: 7, name: "Read Required Textbook Chapter", date: "3 days ago", credits: 5, completed: true },
  ]

  // Achievements with badges
  const achievements = [
    {
      id: 1,
      name: "Perfect Attendance",
      description: "Attended all classes for a month",
      icon: <CheckCircle2 className="h-6 w-6" />,
      color: "bg-green-500",
      date: "Last week",
    },
    {
      id: 2,
      name: "Top Performer",
      description: "Ranked in the top 5% of the class",
      icon: <Star className="h-6 w-6" />,
      color: "bg-yellow-500",
      date: "2 weeks ago",
    },
    {
      id: 3,
      name: "Quick Learner",
      description: "Completed 5 courses ahead of schedule",
      icon: <Zap className="h-6 w-6" />,
      color: "bg-purple-500",
      date: "Last month",
    },
    {
      id: 4,
      name: "Team Player",
      description: "Successfully completed 10 group projects",
      icon: <Trophy className="h-6 w-6" />,
      color: "bg-blue-500",
      date: "2 months ago",
    },
    {
      id: 5,
      name: "Knowledge Seeker",
      description: "Read over 50 academic articles",
      icon: <BookOpen className="h-6 w-6" />,
      color: "bg-cyan-500",
      date: "3 months ago",
    },
    {
      id: 6,
      name: "Consistent Effort",
      description: "Logged study hours every day for 2 months",
      icon: <Flame className="h-6 w-6" />,
      color: "bg-orange-500",
      date: "4 months ago",
    },
    {
      id: 7,
      name: "Critical Thinker",
      description: "Exceptional performance in analytical assessments",
      icon: <Brain className="h-6 w-6" />,
      color: "bg-rose-500",
      date: "5 months ago",
    },
    {
      id: 8,
      name: "Academic Excellence",
      description: "Maintained a GPA above 3.8",
      icon: <Medal className="h-6 w-6" />,
      color: "bg-amber-500",
      date: "6 months ago",
    },
  ]

  // Calculate total credits
  const totalCredits = dailyTasks.reduce((sum, task) => sum + task.credits, 0)

  // Level calculation based on credits
  const level = Math.floor(totalCredits / 20) + 1
  const nextLevelCredits = level * 20
  const levelProgress = ((totalCredits % 20) / 20) * 100

  const handleSave = () => {
    setIsEditing(false)
    // Here you would typically save to your backend
  }

  const handleCancel = () => {
    setIsEditing(false)
    // Reset form data if needed
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-6 xl:p-8">
      {/* space-y: lg/xl 收紧垂直间距，1920 维持 space-y-8 */}
      <div className="max-w-4xl mx-auto space-y-4 md:space-y-6 lg:space-y-6 xl:space-y-8">
        {/* Header */}
        <Card>
          <CardContent className="pt-6">
            {/* gap: lg 缩至 4, xl 缩至 5, 2xl+ 保持 6 */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 lg:gap-4 xl:gap-5 2xl:gap-6 pb-8 md:pb-0">
              <div className="relative">
                {/* avatar: lg=64px xl=80px 2xl+=96px (aspect-square 保持方形) */}
                <Avatar className="w-24 h-24 lg:w-16 lg:h-16 xl:w-20 xl:h-20 2xl:w-24 2xl:h-24">
                  <AvatarImage src={formData.avatarUrl || "/placeholder.svg"} alt="Student profile" />
                  <AvatarFallback className="text-2xl lg:text-base xl:text-xl 2xl:text-2xl">{getCurrentUser()!.name}</AvatarFallback>
                </Avatar>
                {isEditing && (
                  <div className="absolute -bottom-4 left-0 right-0 flex justify-center">
                    <div className="bg-background border rounded-md p-2 shadow-sm w-full max-w-[200px]">
                      <Label htmlFor="avatarUrl" className="text-xs">
                        Avatar URL
                      </Label>
                      <Input
                        id="avatarUrl"
                        value={formData.avatarUrl}
                        onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
                        className="h-7 text-xs"
                        placeholder="Image URL"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 space-y-2">
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="major">Major</Label>
                        <Input
                          id="major"
                          value={formData.major}
                          onChange={(e) => setFormData({ ...formData, major: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="year">Year</Label>
                        <Input
                          id="year"
                          value={formData.year}
                          onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                      {/* name: lg=xl(20px) xl=2xl(24px) 2xl+=3xl(30px) */}
                      <h1 className="text-3xl font-bold lg:text-xl xl:text-2xl 2xl:text-3xl">{formData.name}</h1>
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {formData.major}, {formData.year}
                        </span>
                      </div>
                    </div>
                    <p className="text-muted-foreground max-w-2xl">{formData.bio}</p>
                  </>
                )}

                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="border-green-500 text-green-500">
                    Level {level} Student
                  </Badge>
                  <Badge variant="outline" className="border-amber-500 text-amber-500">
                    {totalCredits} Credits
                  </Badge>
                  <Badge variant="outline" className="border-purple-500 text-purple-500">
                    {achievements.length} Achievements
                  </Badge>
                </div>
              </div>

              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button onClick={handleSave} size="sm">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button onClick={handleCancel} variant="outline" size="sm">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Level Progress */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle>Level Progress</CardTitle>
              <Badge variant="secondary">Level {level}</Badge>
            </div>
            <CardDescription>
              {totalCredits} / {nextLevelCredits} credits to Level {level + 1}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={levelProgress} className="h-2" />
          </CardContent>
          <CardFooter className="text-sm text-muted-foreground">
            Earn {nextLevelCredits - totalCredits} more credits to reach the next level
          </CardFooter>
        </Card>

        {/* Main Content */}
        {/* tabs 内垂直间距: lg 收紧至 4, xl 收紧至 5, 2xl+ 保持 6 */}
        <Tabs defaultValue="credits" className="space-y-4 md:space-y-5 xl:space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="credits">Daily Credits</TabsTrigger>
            <TabsTrigger value="achievements">Achievements</TabsTrigger>
            <TabsTrigger value="info">Student Info</TabsTrigger>
          </TabsList>

          {/* Daily Credits Tab */}
          <TabsContent value="credits" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity & Credits</CardTitle>
                <CardDescription>Track your daily tasks and earned credits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {dailyTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {task.completed ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : (
                            <Clock className="h-5 w-5 text-amber-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{task.name}</p>
                          <p className="text-sm text-muted-foreground">{task.date}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="ml-auto">
                        +{task.credits} Credits
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter>
                <div className="flex justify-between items-center w-full">
                  <span className="text-sm text-muted-foreground">Showing last 7 days of activity</span>
                  <Button variant="outline" size="sm">
                    View All Activity
                  </Button>
                </div>
              </CardFooter>
            </Card>

            {/* Academic Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Academic Statistics</CardTitle>
                <CardDescription>Your academic performance overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* stat cells: p-4→p-3 on lg/xl, number text-2xl→text-lg on lg/xl */}
                  <div className="text-center p-4 lg:p-3 xl:p-4 border rounded-lg">
                    <div className="text-2xl font-bold lg:text-lg xl:text-2xl">3.85</div>
                    <div className="text-sm text-muted-foreground">GPA</div>
                  </div>
                  <div className="text-center p-4 lg:p-3 xl:p-4 border rounded-lg">
                    <div className="text-2xl font-bold lg:text-lg xl:text-2xl">42</div>
                    <div className="text-sm text-muted-foreground">Courses Completed</div>
                  </div>
                  <div className="text-center p-4 lg:p-3 xl:p-4 border rounded-lg">
                    <div className="text-2xl font-bold lg:text-lg xl:text-2xl">95%</div>
                    <div className="text-sm text-muted-foreground">Attendance Rate</div>
                  </div>
                  <div className="text-center p-4 lg:p-3 xl:p-4 border rounded-lg">
                    <div className="text-2xl font-bold lg:text-lg xl:text-2xl">124</div>
                    <div className="text-sm text-muted-foreground">Credit Hours</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Achievements & Badges</CardTitle>
                <CardDescription>Showcase your academic accomplishments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {achievements.map((achievement) => (
                    <div
                      key={achievement.id}
                      className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className={`${achievement.color} p-3 rounded-full text-white`}>{achievement.icon}</div>
                      <div className="flex-1">
                        <h3 className="font-medium">{achievement.name}</h3>
                        <p className="text-sm text-muted-foreground">{achievement.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">Earned: {achievement.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full">
                  <Award className="h-4 w-4 mr-2" />
                  View All Achievements
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* Student Info Tab */}
          <TabsContent value="info" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Student Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Student Information</CardTitle>
                  <CardDescription>Your academic details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="studentId">Student ID</Label>
                        <Input
                          id="studentId"
                          value={formData.studentId}
                          onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                          readOnly
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="location">Residence</Label>
                        <Input
                          id="location"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <Book className="w-4 h-4 text-muted-foreground" />
                        <span>Student ID: {formData.studentId}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span>{formData.email}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{formData.phone}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span>{formData.location}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Course Schedule */}
              <Card>
                <CardHeader>
                  <CardTitle>Current Schedule</CardTitle>
                  <CardDescription>Your enrolled courses this semester</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">CS 401: Advanced Algorithms</p>
                        <p className="text-sm text-muted-foreground">Mon, Wed, Fri • 10:00 AM - 11:15 AM</p>
                      </div>
                      <Badge>A-</Badge>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">MATH 302: Linear Algebra</p>
                        <p className="text-sm text-muted-foreground">Tue, Thu • 1:00 PM - 2:30 PM</p>
                      </div>
                      <Badge>B+</Badge>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">CS 450: Machine Learning</p>
                        <p className="text-sm text-muted-foreground">Mon, Wed • 2:00 PM - 3:30 PM</p>
                      </div>
                      <Badge>A</Badge>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">PHYS 201: Modern Physics</p>
                        <p className="text-sm text-muted-foreground">Tue, Thu • 9:00 AM - 10:30 AM</p>
                      </div>
                      <Badge>B</Badge>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">
                    <Calendar className="h-4 w-4 mr-2" />
                    View Full Schedule
                  </Button>
                </CardFooter>
              </Card>
            </div>

            {/* Academic Goals */}
            <Card>
              <CardHeader>
                <CardTitle>Academic Goals</CardTitle>
                <CardDescription>Track your progress toward academic goals</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Complete Senior Project</Label>
                      <span className="text-sm text-muted-foreground">65%</span>
                    </div>
                    <Progress value={65} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Maintain 3.8+ GPA</Label>
                      <span className="text-sm text-muted-foreground">90%</span>
                    </div>
                    <Progress value={90} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Complete Internship Hours</Label>
                      <span className="text-sm text-muted-foreground">40%</span>
                    </div>
                    <Progress value={40} className="h-2" />
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full">
                  <Target className="h-4 w-4 mr-2" />
                  Set New Goal
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
